// 覚醒王・攻撃していない間の間合いを「技の選び方」と「区切りのある一手」で作る (09-26 比較版 → 09-27 通常起動・配布版の既定)。
//   ユーザー原文 (09-26):「今みたいな距離の調整をするんじゃなくて攻撃の始めや終わり、攻撃と攻撃の間の距離から調整など
//     今の方法とは違う方法で調整できないかな」→「OK、その1〜4で覚醒王の比較版を作って」。
//   09-27 決裁箱 D1「覚醒王の間合いの比較版 … を採りますか？」→ ユーザー「本番に採る」。
//     旧の動き (追従の歩き・09-18 の立て直し = 8e66b9e1 以降の配布版) は DEV `?spacing=old` だけで残す。
//   1 動かない幅 (近い境界〜遠い境界) では構えて待つだけ。
//   2 距離で技を選ぶ: 連ごとに「攻撃に入れる距離」と距離帯 (近・中・遠) の重みを持つ。遠くても突進で届けば歩かない。
//   3 技の終わりに下がる: 近すぎたら recovery を最後まで見せた後、1回だけ後ろ跳び (壁際では跳ばない)。
//   4 技と技の間は一手: 待つ／後ろ跳び／歩いて詰める から1つ。どれにも時間の上限がある。
//   核心: 待機中に本体が動くのは「歩いて詰める」(歩きの絵) か「後ろ跳び」(仮組みの絵) の最中だけ。立ち絵のまま滑らない。
//   数値はすべて AI の仮設定 (票 docs/handoffs/2026-09-26-awoken-spacing-choice.md の表)。
//   本番: 覚醒王なら常にこの動き。本番ビルドでは usesAwokenSpacingChoice が `boss.id === BOSS_ID` だけに畳まれ、
//     DEV の切り替え (enabled) は参照されない。DEV は game.ts の箱が `?spacing=old` の時だけ旧へ戻す。
//   既定: モジュールの既定も on。壁跳ね (awoken_wall_hunt_game.ts) は「モジュール既定 off・main で on」だが、
//     こちらはテスト (vitest) が何もしなくても本番と同じ動きを測るよう、既定を on にしている。
//     旧を測るテストは setAwokenSpacingChoice(false) を明示し、後片付けで true に戻す。
import { bossBodyBoundsFor, FIXED_DT } from './balance'
import type { Boss, BossString, Player } from './types'
import { AWOKEN_BITE_REST_FORWARD_PX, awokenBiteMinReachPx } from './awoken_bite_mouth'
import { AWOKEN_FACE_ROOM_SLACK_PX } from './awoken_face_room'
import { spacingAllTuning, type SpacingTuning } from './spacing_all'

const BOSS_ID = 'crowned-king-awoken'
let enabled = true
let temporaryHopArt = false
// 2026-10-05 着地の滑り (ユーザー「着地後ほんの少しだけ滑らせてOK」・案BのFXの跡と合わせる)。量は候補からユーザーが選ぶ。
//   DEV `?awokenHopSlide=0|10|20|30` で比較。滑りがある時は着地の屈みを 1px あたり 2ms 延ばし、その間に減速して止まる
//   (絵は着地の1枚のまま = 立ち絵では滑らない)。宙の終わりの速さを滑りの出だしの速さに揃える (止まってから急に滑らない)。
export const AWOKEN_HOP_SLIDE_CHOICES = [0, 10, 20, 30] as const
let hopSlidePx = 30   // 2026-10-05 ユーザー「30pxで決定」
/** DEV comparison only. */
export function setAwokenHopSlidePx(value: number): void {
  if (import.meta.env.DEV && Number.isFinite(value) && value >= 0 && value <= 60) hopSlidePx = value
}
export function awokenHopSlidePx(): number { return hopSlidePx }
/** 着地の屈みの長さ (滑りの分だけ延びる)。 */
export function awokenHopLandMs(): number { return SPACING.hop.landMs + 2 * hopSlidePx }
/** DEV comparison only. Production always uses the dedicated back-hop pictures. */
export function setAwokenHopTemporaryArt(value: boolean): void {
  if (import.meta.env.DEV) temporaryHopArt = value
}
/** DEV だけ効く (本番では何もしない)。false = 旧の動き (`?spacing=old`)。 */
export function setAwokenSpacingChoice(value: boolean): void { if (import.meta.env.DEV) enabled = value }
export function usesAwokenSpacingChoice(boss: Pick<Boss, 'id'>): boolean {
  return boss.id === BOSS_ID && (!import.meta.env.DEV || enabled)
}
/** 待機の一手で動くか (覚醒王 + 戴冠獣・亡霊・花嫁 = src/spacing_all.ts・11-01 から配布版)。技の選び方は覚醒王だけ (usesAwokenSpacingChoice)。 */
export function usesSpacingChoice(boss: Pick<Boss, 'id'>): boolean {
  return usesAwokenSpacingChoice(boss) || spacingAllTuning(boss.id) !== null
}

/** 仮設定 (AI)。距離はボス中心と主人公の x の差 (px)。 */
// 2026-09-26 噛みは口の周りだけ当たる (awoken_bite_mouth.ts) に合わせて口の距離から引き直した:
//   近い境界 = 暴噛の口が届く一番近い間合い (146・旧 150)、後ろ跳び必須 = どの噛みも届かない間合い (116・旧 110)。
// 2026-09-27 噛みは体の前1/3でも当たる (同ファイル AWOKEN_BITE_FRONT_THIRD) ので、同じ式のまま両方 53.7 へ縮んだ
//   (暴噛の懐 = どの噛みも届かない懐 = 前1/3 の近い端 68.7 − 主人公の半幅 15)。近の帯 (〜53.7) は後ろ跳び必須の帯とほぼ重なる。
// 噛みが届く一番遠い間合い = 踏み込み (lungeDistance・attacks.ts と同じ値) を使い切って口が主人公に重なる所
//   = 着地の口の前方距離 + 踏み込み (2026-09-26 引き直し。旧 = 胴基準の 250/260/240)。暴噛 326・溜め 307・連噛 276。
const BITE_LUNGE_PX: Record<string, number> = { feralBite: 150, feralBiteDelayed: 160, feralFrenzy: 130, feralFrenzyExtend: 130 }
const biteReachPx = (attackId: string) => AWOKEN_BITE_REST_FORWARD_PX[attackId]! + BITE_LUNGE_PX[attackId]!
const BITE_POCKET_PX = awokenBiteMinReachPx('feralBite')!
const ANY_BITE_POCKET_PX = Math.min(awokenBiteMinReachPx('feralFrenzy')!, awokenBiteMinReachPx('feralBiteDelayed')!)
export const SPACING = {
  nearPx: BITE_POCKET_PX,       // 近い境界。これ以下が「近」 (暴噛は届かない)。09-27 から 53.7 (09-26 は 146)
  farPx: biteReachPx('feralFrenzy'),   // 遠い境界 (276・旧 260)。これより上が「遠」= 噛みの一部が届かない。近い境界〜ここが動かない幅
  tooClosePx: ANY_BITE_POCKET_PX,   // 技の終わりにこれより近ければ、後ろ跳びを必ず選ぶ (どの噛みも届かない・冷却中・壁際は除く)。09-27 から 53.7 (09-26 は 116)
  hopChanceNear: 0.35,  // 近 (tooClosePx〜nearPx。09-26 は 116〜146・09-27 から幅ほぼ0) の一手で後ろ跳びを選ぶ確率 (残りは待つ)
  walkChanceFar: 0.4,   // 遠で届く技がある時に「歩いて詰める」を選ぶ確率 (残りは待って届く技を出す)
  walkStopPx: 220,      // 自分から歩いて詰めた時に止まる距離 (中の帯へ入る)
  walkMinPx: 130,       // 1歩分 (歩きの絵1サイクル) 未満の歩きは選ばない
  walkFloorPx: 100,     // 歩きの止まる距離の下限 (これより近くへは歩かない)
  walkMaxMs: 1500,      // 歩きの時間上限。過ぎたら考え直す
  hop: {
    distancePx: 110,    // 跳ぶ距離
    crouchMs: 90,       // 踏み切りの屈み (本体は動かない)
    airMs: 230,         // 宙 (ここだけ本体が動く)
    landMs: 110,        // 着地の屈み (本体は動かない)
    heightPx: 28,       // 宙の最高点 (当たり判定の「空中で当たらない」72px より低い)
    cooldownMs: 3500,   // 跳び終えてから次に跳べるまで (連発しない)
  },
} as const
const AWOKEN_HOP_POSE = { crouch: 'back_hop_crouch_1001', air: 'back_hop_air_1001', land: 'back_hop_land_1001' }
function tuningFor(boss: Pick<Boss, 'id'>): SpacingTuning {
  const other = boss.id !== BOSS_ID ? spacingAllTuning(boss.id) : null
  return other ?? { ...SPACING, slidePx: hopSlidePx, pose: AWOKEN_HOP_POSE }
}
const landMsOf = (t: SpacingTuning) => t.hop.landMs + 2 * t.slidePx
const hopTotalMs = (t: SpacingTuning) => t.hop.crouchMs + t.hop.airMs + landMsOf(t)

type Band = 'near' | 'mid' | 'far'
export function spacingBand(distance: number): Band {
  return distance <= SPACING.nearPx ? 'near' : distance <= SPACING.farPx ? 'mid' : 'far'
}

/** 連ごとの「攻撃に入れる距離」と距離帯の重み (元の weight に掛ける)。 */
export const SPACING_STRINGS: Record<string, { reachPx: number; near: number; mid: number; far: number }> = {
  // 噛み系: 口の距離 + 踏み込みまで届く。近 (nearPx 以下) では暴噛は届かない (懐では噛まない・boss.ts が候補から外す)。
  'ak-bite': { reachPx: biteReachPx('feralBite'), near: 1.0, mid: 1.2, far: 1 },
  'ak-bite-bite': { reachPx: biteReachPx('feralBite'), near: 1.0, mid: 1.2, far: 1 },
  'ak-bite-delay': { reachPx: biteReachPx('feralBiteDelayed'), near: 0.8, mid: 1.3, far: 1 },
  'ak-bite-frenzy': { reachPx: biteReachPx('feralBite'), near: 1.0, mid: 1.1, far: 1 },
  'ak-bite-bite-rush': { reachPx: biteReachPx('feralBite'), near: 1.0, mid: 1.1, far: 1 },
  // 連噛み: 踏み込み 130px。近で出やすい (09-26 は 116〜146 で連噛みだけが届いた。09-27 の前1/3 からは4技の懐が同じ 53.7 なので
  //   近の重みが効く帯はほぼ無い)。伸びは旧 preferClose 相当 (近 1.8・中 0.15)。
  'ak-frenzy': { reachPx: biteReachPx('feralFrenzy'), near: 1.3, mid: 0.8, far: 1 },
  'ak-frenzy-extend': { reachPx: biteReachPx('feralFrenzyExtend'), near: 1.8, mid: 0.15, far: 1 },
  // 突進系: 踏み込み 330px で遠くから一気に詰める (遠で届くのは実質これと壁跳ね・大暴れだけ)。
  //   近の重みは下げない: 07-07 独立レビューP1「近接プレイで主教材の赤 (突進) が構造的に出なくなる」を再発させない。
  'ak-rush': { reachPx: 450, near: 1.0, mid: 1.2, far: 2.0 },
  'ak-rush-chase': { reachPx: 450, near: 1.0, mid: 1.2, far: 2.0 },
  // 壁跳ね・大暴れ: 距離を問わず出せる (走路・台本が位置を決める)。
  'ak-wall-rush': { reachPx: Infinity, near: 0.8, mid: 1.0, far: 1.5 },
  'ak-rampage': { reachPx: Infinity, near: 1, mid: 1, far: 1 },
}
const DEFAULT_ENTRY = { reachPx: 210, near: 1, mid: 1, far: 1 }
const entryOf = (s: Pick<BossString, 'id'>) => SPACING_STRINGS[s.id] ?? DEFAULT_ENTRY

/** その連を今の距離から出せるか。 */
export function spacingReaches(s: Pick<BossString, 'id'>, distance: number): boolean {
  return distance <= entryOf(s).reachPx
}
/** 候補の中で一番遠くから出せる距離 (候補なし = 0)。 */
export function spacingReachPx(candidates: Pick<BossString, 'id'>[]): number {
  return candidates.reduce((m, s) => Math.max(m, entryOf(s).reachPx), 0)
}
/** 距離帯の重み。届かない連は 0。 */
export function spacingWeight(s: Pick<BossString, 'id' | 'weight'>, distance: number): number {
  if (!spacingReaches(s, distance)) return 0
  const e = entryOf(s)
  return s.weight * e[spacingBand(distance)]
}

export type AwokenSpacing = {
  action: 'wait' | 'hop' | 'walk'
  elapsedMs: number
  // hop: 起点・終点。walk: 止まる距離・向き。
  fromX: number
  toX: number
  dir: 1 | -1
  stopPx: number
  // 一手を終えた (hop/walk の後は待つだけ・次は技)。
  done: boolean
  // walk: 止まる距離へ着く最後の1歩を踏んだ (次のtickで歩きを終える。相手が動いても歩き直さない)。
  arriving?: boolean
  // 顔を見せる間合い (DEV ?awokenFaceRoom=1) の後ろ歩きを、この待機で使い終えた。
  faceRoomUsed?: boolean
}

/** 待機を抜けたら一手を片付ける。跳びの冷却は残す。 */
export function clearAwokenSpacing(boss: Boss): Boss {
  if (!boss.spacing) return boss
  const { spacing: _old, ...rest } = boss
  return rest
}

/** 毎tickの頭: 跳びの冷却を減らす。覚醒王以外・DEV `?spacing=old` では何もしない (同じオブジェクトを返す)。 */
//   崩れ (staggerBoss) は enterState を通らないので、待機以外で残った一手もここで片付ける。
export function prepareAwokenSpacing(boss: Boss): Boss {
  if (!usesSpacingChoice(boss)) return boss
  const cd = boss.spacingHopCooldownMs ?? 0
  const next = boss.state === 'idle' ? boss : clearAwokenSpacing(boss)
  return cd > 0 ? { ...next, spacingHopCooldownMs: Math.max(0, cd - FIXED_DT * 1000) } : next
}

/** 後ろ跳びの最中は向きを変えない (宙で振り向かない)。 */
export function awokenSpacingLocksFacing(boss: Boss): boolean {
  return boss.state === 'idle' && boss.spacing?.action === 'hop' && !boss.spacing.done
}

/** Dedicated crouch / air / landing art. DEV can restore the original temporary pictures. */
export function awokenSpacingPoseKey(boss: Boss): string | null {
  const s = boss.spacing
  if (boss.state !== 'idle' || !s || s.action !== 'hop') return null
  const t = tuningFor(boss)
  if (!t.pose) return null
  if (s.done) {
    // 着地を終えた後の起き上がり (pose.rise がある時だけ)。待機中で、次の一手へ替わるまで。
    const rise = t.pose.rise
    if (!rise) return null
    const i = Math.floor((s.elapsedMs - hopTotalMs(t)) / rise.stepMs)
    return i >= 0 && i < rise.keys.length ? rise.keys[i] : null
  }
  const { crouchMs, airMs } = t.hop
  // 宙の絵 = 本体が動くtick全部 (宙に入った最初のtickから、残りを詰め切るtickまで)。
  const inAir = s.elapsedMs > crouchMs && s.elapsedMs - FIXED_DT * 1000 < crouchMs + airMs
  if (import.meta.env.DEV && temporaryHopArt && boss.id === BOSS_ID) return inAir ? 'rampage_leap' : 'dash_crouch_plain_0920'
  if (inAir) return t.pose.air
  return s.elapsedMs <= crouchMs ? t.pose.crouch : t.pose.land
}
/** 絵の側: 歩いて詰める一手の最中 (歩き始めから止まるまで歩きの絵。130px の下限を通さない)。 */
export function isAwokenSpacingWalk(boss: Boss): boolean {
  return boss.state === 'idle' && boss.spacing?.action === 'walk' && !boss.spacing.done
}

function easeInOut(p: number): number {
  const t = Math.max(0, Math.min(1, p))
  return t < 0.5 ? 2 * t * t : 1 - ((-2 * t + 2) ** 2) / 2
}

export type SpacingContext = {
  walkSpeed: number
  gapMs: number               // 既存の攻撃間隔 (待つの上限もこれ)
  reachPx: number             // 今の候補の中で一番遠くから出せる距離
  forced: boolean             // 確定初手の待ち: 跳ばない・待たずに届く所まで歩く
  // 懐では噛まない (2026-09-26 ユーザー裁定): 出せる連が懐で届かない噛みだけの時、歩いて下がって止まる距離。
  retreatToPx?: number
  // 顔を見せる間合い (DEV ?awokenFaceRoom=1・src/awoken_face_room.ts)。主人公がこれより近ければ、待機ごとに1回だけ歩いて下がる。
  faceRoomPx?: number
  clampWalk: (x: number, fromX: number, dir: 1 | -1) => number   // 主人公を追い越さない・壁
}

/**
 * idle の毎tick (覚醒王だけ・DEV `?spacing=old` を除く)。一手を進め、「今攻撃してよいか (届く・一手が終わっている)」を返す。
 * 攻撃間隔 (gapMs) の判定と技の選択は呼び出し側 (boss.ts) のまま。
 */
export function updateAwokenSpacing(boss: Boss, player: Player, ctx: SpacingContext): { boss: Boss; allowAttack: boolean } {
  const dtMs = FIXED_DT * 1000
  const T = tuningFor(boss)
  // 体の壁 (bossBodyBoundsFor)。覚醒王は部屋の壁の 100 内側 (2026-10-08 ユーザー採用 F-1・DEV ?awokenWall=0 で旧)。後ろ跳びが見えない壁で途中で止まらない (10-09 R1)。
  const walls = bossBodyBoundsFor(boss.id)
  const distance = Math.abs(player.x - boss.x)
  const toward: 1 | -1 = player.x < boss.x ? -1 : 1
  const idleMs = boss.stateElapsed * 1000
  let next = boss
  let s = boss.spacing

  const startWalk = (stopPx: number, minPx: number = T.walkMinPx): AwokenSpacing | null => {
    const stop = Math.max(T.walkFloorPx, Math.min(stopPx, distance - minPx))
    if (distance - stop < minPx - 1e-6) return null
    return { action: 'walk', elapsedMs: 0, fromX: boss.x, toX: boss.x, dir: toward, stopPx: stop, done: false }
  }
  const startHop = (): AwokenSpacing | null => {
    if ((boss.spacingHopCooldownMs ?? 0) > 0) return null
    const away = -toward as 1 | -1
    const room = away > 0 ? walls.rightWall - boss.x : boss.x - walls.leftWall
    if (room < T.hop.distancePx + T.slidePx) return null   // 壁で下がれない時は跳ばない (着地の滑りの分も)
    return { action: 'hop', elapsedMs: 0, fromX: boss.x, toX: boss.x + away * T.hop.distancePx,
      dir: away, stopPx: 0, done: false }
  }
  const wait = (): AwokenSpacing => ({ action: 'wait', elapsedMs: 0, fromX: boss.x, toX: boss.x, dir: toward, stopPx: 0, done: false })

  // 一手を決める (待機に入った最初のtick = 技の終わり・崩れの後・戦闘開始)。
  if (!s) {
    if (ctx.forced) {
      s = distance > ctx.reachPx ? startWalk(ctx.reachPx - 10) ?? wait() : wait()
    } else if (distance < T.tooClosePx) {
      s = startHop() ?? wait()                                    // 3 技の終わりに下がる
    } else if (distance <= T.nearPx) {
      s = Math.random() < T.hopChanceNear ? startHop() ?? wait() : wait()   // 4 近: 跳ぶ／待つ
    } else if (distance <= T.farPx) {
      s = wait()                                                  // 1 動かない幅: 構えて待つだけ
    } else if (distance > ctx.reachPx) {
      s = startWalk(Math.min(T.walkStopPx, ctx.reachPx - 10)) ?? wait()   // 届く技が無い: 歩いて詰める
    } else {
      s = Math.random() < T.walkChanceFar ? startWalk(T.walkStopPx) ?? wait() : wait()   // 4 遠: 歩く／待って突進
    }
  }
  // 懐では噛まない: 後ろ跳びの最中でなければ、口が届く所まで歩いて下がる (歩きの絵のまま・跳ばない)。
  if (ctx.retreatToPx !== undefined && distance < ctx.retreatToPx && !(s.action === 'hop' && !s.done)
      && !(s.action === 'walk' && !s.done && s.dir !== toward)) {
    s = { action: 'walk', elapsedMs: 0, fromX: boss.x, toX: boss.x, dir: -toward as 1 | -1, stopPx: ctx.retreatToPx, done: false,
      faceRoomUsed: s.faceRoomUsed }
  }
  // 顔を見せる間合い (比較版 DEV ?awokenFaceRoom=1): 予備動作の顔が主人公に重ならない所まで、待機ごとに1回だけ歩いて下がる。
  if (ctx.faceRoomPx !== undefined && !s.faceRoomUsed && distance < ctx.faceRoomPx - AWOKEN_FACE_ROOM_SLACK_PX
      && (s.done || s.action === 'wait')) {
    s = { action: 'walk', elapsedMs: 0, fromX: boss.x, toX: boss.x, dir: -toward as 1 | -1, stopPx: ctx.faceRoomPx, done: false,
      faceRoomUsed: true }
  }
  s = { ...s, elapsedMs: s.elapsedMs + dtMs }

  if (s.action === 'hop' && !s.done) {
    const { crouchMs, airMs, heightPx, cooldownMs } = T.hop
    const p = (s.elapsedMs - crouchMs) / airMs
    const landMs = landMsOf(T)
    // 滑りの出だしの速さ (減速して landMs で止まる) = 宙の終わりの速さ。滑りなしなら今までの easeInOut そのもの。
    //   p^3 - p^2 は両端で 0・出だしの傾き 0・終わりの傾き 1 = 跳ぶ距離と踏み切りを変えずに、終わりの速さだけ足す。
    const endSlope = T.slidePx > 0 ? (2 * T.slidePx / landMs) * airMs / T.hop.distancePx : 0
    // 滑りは着地の最後の1tickより前に止め切る (一手を終えるtick = 立ち絵へ戻るtickでは動かない)。
    const q = Math.max(0, Math.min(1, (s.elapsedMs - crouchMs - airMs) / (landMs - dtMs)))
    const pc = Math.max(0, Math.min(1, p))
    const x = p < 1
      ? s.fromX + (s.toX - s.fromX) * (easeInOut(pc) + endSlope * (pc ** 3 - pc ** 2))
      : s.toX + s.dir * T.slidePx * (1 - (1 - q) ** 2)
    const altitudePx = p > 0 && p < 1 ? heightPx * Math.sin(Math.PI * p) : 0
    const landed = s.elapsedMs >= hopTotalMs(T)
    next = { ...next, x: Math.max(walls.leftWall, Math.min(walls.rightWall, x)), altitudePx,
      spacing: { ...s, done: landed },
      ...(landed ? { spacingHopCooldownMs: cooldownMs } : {}) }
    return { boss: next, allowAttack: false }
  }

  if (s.action === 'walk' && !s.done) {
    // back = 懐から歩いて下がる一手 (相手から離れる向き)。止まるのは stopPx 以上離れた所。
    const back = s.dir !== toward
    const flanked = !back && (player.x - boss.x) * s.dir <= 0
    // 2026-09-27 (検収 A-1「距離が詰まった時に…スローモーションになってて不自然」): 止まる距離へ着く最後の1歩を
    //   踏んだら (arriving)、次のtickで歩きを終える。旧は「残り」だけ進む1歩を毎tick繰り返したため、
    //   相手が同じ向きに動き続けると (下がる主人公を追う・詰めてくる主人公から下がる) ボスが相手の速さ
    //   (120px/s = 歩きの39%) で歩き続け、歩きの絵も距離に合わせて遅く回った (最長 walkMaxMs の 1.5秒)。
    //   → 着いたら構えて待つ。相手がまた離れても、同じ一手の中では歩き直さない (次は技か、次の一手)。
    //   壁・主人公の手前で1歩が削られた時も同じ (削られた1歩を最後に歩きを終える)。
    const arrived = s.arriving === true || (back ? distance >= s.stopPx : distance <= s.stopPx)
    if (flanked || arrived || s.elapsedMs > T.walkMaxMs) {
      s = { ...s, done: true }
    } else {
      const remaining = back ? s.stopPx - distance : distance - s.stopPx
      const fullStep = ctx.walkSpeed * FIXED_DT
      const step = Math.min(remaining, fullStep)
      const raw = boss.x + s.dir * step
      const x = Math.max(walls.leftWall, Math.min(walls.rightWall, ctx.clampWalk(raw, boss.x, s.dir)))
      if (Math.abs(x - boss.x) < 1e-6) {
        s = { ...s, done: true }                                  // 壁・主人公で止められた
      } else {
        const last = remaining <= fullStep || Math.abs(x - boss.x) < step - 1e-6
        return { boss: { ...next, x, spacing: last ? { ...s, arriving: true } : s }, allowAttack: false }
      }
    }
    // 歩き切った後は、時間上限で打ち切った時だけ考え直す (次のtickで一手を選び直す)。
    if (distance > ctx.reachPx && !flanked) {
      return { boss: clearAwokenSpacing(next), allowAttack: false }
    }
    return { boss: { ...next, spacing: s }, allowAttack: distance <= ctx.reachPx }
  }

  // 待つ (または一手を終えた後)。待ちの上限 = 既存の攻撃間隔。過ぎても届かなければ歩いて詰める。
  if (idleMs >= ctx.gapMs && distance > ctx.reachPx) {
    const stopPx = ctx.forced ? ctx.reachPx - 10 : Math.min(T.walkStopPx, ctx.reachPx - 10)
    // 2026-10-05 (board awoken-spacing-stall-1005): 距離が「届く距離より少し遠い・1歩 (walkMinPx) 未満」だと
    //   1歩の歩きが選べず、攻撃も歩きも出ないまま立ち止まった相手の前で永久に構え続けた
    //   (tests/combat_sim/awoken_spacing_choice.test.ts noWalk seed1・主人公3段目 1.0s の枝で 180秒停止)。
    //   待ちの上限を過ぎた時だけ、1歩未満でも届く所まで詰める (普段の一手の選び方は変えない)。
    const walk = startWalk(stopPx) ?? startWalk(stopPx, 1)
    if (walk) return updateAwokenSpacing({ ...next, spacing: walk }, player, ctx)
  }
  return { boss: { ...next, spacing: s }, allowAttack: distance <= ctx.reachPx }
}
